import type { ReactNode } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * A bordered block that opens and closes on its title — MUI's Accordion in the
 * one shape this app uses. The title row carries the counts, so a closed block
 * still says how much is inside; the open body stops at `maxHeight` and
 * scrolls, so a long list never pushes the rest of the page out of reach.
 */
export function ExpandableSection({
    title,
    counts,
    defaultExpanded = false,
    expanded,
    onChange,
    maxHeight = 360,
    tone = 'divider',
    children
}: {
    title: ReactNode;
    /** Chips or short text beside the title — visible while closed. */
    counts?: ReactNode;
    defaultExpanded?: boolean;
    /** Set to open or close it from outside; it then follows this value. */
    expanded?: boolean;
    onChange?: (expanded: boolean) => void;
    /** `null` when the content scrolls itself (a data grid, a text field). */
    maxHeight?: number | null;
    /** The border colour: `warning.main`, `error.main`, … */
    tone?: string;
    children: ReactNode;
}) {
    return (
        <Accordion
            variant="outlined"
            disableGutters
            defaultExpanded={defaultExpanded}
            // Spread only when given: an explicit `undefined` would still
            // count as a prop to MUI.
            {...(expanded !== undefined && { expanded })}
            onChange={(_event, isExpanded) => onChange?.(isExpanded)}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{
                mt: 2,
                borderColor: tone,
                borderRadius: 1,
                // The divider line MUI draws between stacked accordions;
                // these are separate blocks with their own border.
                '&::before': { display: 'none' }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap'
                    }}
                >
                    <Typography variant="subtitle1" component="span">
                        {title}
                    </Typography>
                    {counts && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.75,
                                flexWrap: 'wrap'
                            }}
                        >
                            {counts}
                        </Box>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    pt: 0,
                    ...(maxHeight !== null && { maxHeight, overflowY: 'auto' })
                }}
            >
                {children}
            </AccordionDetails>
        </Accordion>
    );
}
